import React, { useState, useEffect } from 'react';
import i18nIsoCountries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import CustomTable from 'components/CustomTable/CustomTable';
import { theme, Button, notification, Modal } from 'antd';
import { article8Op } from 'store/ducks/DataManagement/Article8AndICTSystem';
import { IArticle8Types } from 'store/ducks/DataManagement/Article8AndICTSystem/types';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import { getArticle8Columns } from 'components/Columns/Article8TableColumns';
import SaveButton from 'components/SaveButton/SaveButton';
import * as Styled from '../../DataEntry/DataEntry.styled';
import { useTranslation } from 'react-i18next';
import { deleteRecord } from 'utils/hooks/deleteRecord';
import useFilters from 'utils/hooks/useFilters';

const ICTSystemCategory: React.FC = () => {
  i18nIsoCountries.registerLocale(enLocale);
  const { query } = useFilters();
  const { useToken } = theme;
  const [dataSource, setDataSource] = useState<IArticle8Types[]>([]);
  const [count, setCount] = useState(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState(10);
  const { token } = useToken();
  const isLoggedIn = localStorage.getItem('authToken');
  const { confirm } = Modal;
  const { t } = useTranslation();

  const handlePageChange = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const handleDateChange = (key: number, field: string, dateString: string) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => key === item.key);
    if (index > -1) {
      const item = newData[index];
      newData.splice(index, 1, {
        ...item,
        [field]: dateString,
        isEdited: !item.isNew,
      });
      setDataSource(newData);
    }
  };

  const fetchICTSystemCriticalityByEntity = async () => {
    try {
      const response = await article8Op.getICTSystemCriticality();
      const data = response?.data?.ict_system_criticalities;

      const mappedData = Array.isArray(data)
        ? data.map((item, index) => ({
            key: index,
            active: item.active,
            deleted: item.deleted,
            start_date: item.start_date,
            end_date: item.end_date,
            description: item.description,
            id: item.id,
            name: item.name,
            isNew: false,
            isEdited: false,
          }))
        : data
          ? [
              {
                key: 0,
                active: data.active,
                deleted: data.deleted,
                start_date: data.start_date,
                end_date: data.end_date,
                description: data.description,
                id: data.id,
                name: data.name,
                isNew: false,
                isEdited: false,
              },
            ]
          : [];

      setDataSource(mappedData);
      setCount(mappedData.length);
    } catch (error) {
      setDataSource([]);
    }
  };

  useEffect(() => {
    if (query.entity) {
      fetchICTSystemCriticalityByEntity();
    } else {
      setDataSource([]);
    }
  }, [query.entity]);

  const handleAddRow = (record?: any, index?: number) => {
    const newData: IArticle8Types = {
      key: generateUniqueId(),
      active: true,
      deleted: false,
      start_date: '',
      end_date: '9999-01-01',
      description: '',
      id: generateUniqueId(),
      name: '',
      isNew: true,
    };
    const insertIndex = dataSource.findIndex(
      (item) => item.key === record?.key,
    );

    const updatedDataSource = [
      ...dataSource.slice(0, insertIndex + 1),
      newData,
      ...dataSource.slice(insertIndex + 1),
    ];

    setDataSource(updatedDataSource);
    setCount(count + 1);
  };

  const handleSave = (row: IArticle8Types) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    newData.splice(index, 1, {
      ...newData[index],
      ...row,
      isEdited: !newData[index].isNew,
    });
    setDataSource(newData);
  };

  const handleRemoveRow = (key: number) => {
    Modal.confirm({
      title: 'Are you sure you want to remove this row?',
      content:
        'Once deleted, this record cannot be restored. Would you like to continue?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => {
        const updatedDataSource = dataSource.filter((item) => item.key !== key);
        setDataSource(updatedDataSource);
        notification.success({
          message: 'Info',
          description: 'The row has been successfully removed.',
          duration: 20,
        });
      },
      onCancel: () => {
        notification.info({
          message: 'Info',
          description: 'The row removal has been canceled.',
          duration: 20,
        });
      },
    });
  };

  const handleInactivate = (record: IArticle8Types) => {
    if (!record.id) {
      notification.error({
        message: 'Info',
        description: 'Id is missing.',
      });
      return;
    }

    deleteRecord({
      id: record.id,
      tableName: 'ict_system_criticality',
      onSuccess: () => {
        fetchICTSystemCriticalityByEntity();
      },
    });
  };

  const columns = getArticle8Columns(
    handleDateChange,
    handleAddRow,
    handleRemoveRow,
    handleInactivate,
    t,
  );

  const handleSubmit = async () => {
    const changedData = dataSource.filter(
      (item) => item.isNew || item.isEdited,
    );

    if (changedData.length === 0) {
      notification.info({
        message: 'Info',
        description: 'There are no new or edited data to submit.',
        placement: 'topRight',
        duration: 20,
      });
      return;
    }

    const formattedData = {
      data_list: changedData.map((item) => ({
        id: item.id,
        name: item.name,
        description: item.description,
        start_date: item.start_date,
        end_date: item.end_date,
      })),
      token: isLoggedIn,
    };

    try {
      const response =
        await article8Op.insertICTSystemCriticality(formattedData);
      if (response?.status === 200) {
        setDataSource((prev) =>
          prev.map((item) =>
            item.isNew || item.isEdited
              ? { ...item, isNew: false, isEdited: false }
              : item,
          ),
        );
        notification.success({
          message: 'Info',
          description: 'The data was successfully submitted.',
          placement: 'topRight',
          duration: 20,
        });
      }
    } catch (error) {
      notification.error({
        message: 'Info',
        description: 'There was an error sending the data. Please try again.',
        placement: 'topRight',
        duration: 20,
      });
    }
  };

  return (
    dataSource && (
      <>
        <Styled.HighlightedTable>
          <CustomTable
            columns={columns}
            dataSource={dataSource}
            handleSave={handleSave}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              onChange: handlePageChange,
              showSizeChanger: true,
            }}
            locale={{
              emptyText: (
                <Button type="primary" onClick={() => handleAddRow()}>
                  {t('GENERAL.ADD_FIRST_ROW')}
                </Button>
              ),
            }}
          />
        </Styled.HighlightedTable>

        <Styled.ButtonRow>
          {dataSource.length > 0 && (
            <Styled.AreaButton
              token={token}
              type="primary"
              onClick={() => handleAddRow()}
            >
              Add new row
            </Styled.AreaButton>
          )}
          {dataSource.length > 0 && (
            <SaveButton
              token={token}
              hasChanges={dataSource.some((i) => i.isNew || i.isEdited)}
              onClick={handleSubmit}
            />
          )}
        </Styled.ButtonRow>
      </>
    )
  );
};

export default ICTSystemCategory;
